import type { SagaAuditLogDto } from './ErrorHandlingSaga'

// Mirrors the server SagaWorkflowGraphDto. SagaNodeKind/Status are serialized as numbers (same as the other saga
// enums); eventType is emitted as the enum name string (the server uses a per-property JsonStringEnumConverter).

// [Flags] — a node can combine roles (e.g. Start | Wait). Normal (0) carries no glyph.
export const SagaNodeKind = {
  Normal: 0,
  Start: 1,
  Wait: 2,
  TerminalOk: 4,
  TerminalFail: 8,
} as const
export type SagaNodeKind = typeof SagaNodeKind[keyof typeof SagaNodeKind]

export const SagaNodeStatus = {
  Pending: 0,
  Active: 1,
  Done: 2,
  Failed: 3,
} as const
export type SagaNodeStatus = typeof SagaNodeStatus[keyof typeof SagaNodeStatus]

// Chakra colorScheme per node status — aligned with the StateBadge / attempt badges used elsewhere on the screen.
export const sagaNodeStatusColor: Record<SagaNodeStatus, string> = {
  [SagaNodeStatus.Pending]: 'gray',
  [SagaNodeStatus.Active]: 'blue',
  [SagaNodeStatus.Done]: 'green',
  [SagaNodeStatus.Failed]: 'red',
}

export const sagaNodeStatusLabel: Record<SagaNodeStatus, string> = {
  [SagaNodeStatus.Pending]: 'Čeká',
  [SagaNodeStatus.Active]: 'Aktuální',
  [SagaNodeStatus.Done]: 'Hotovo',
  [SagaNodeStatus.Failed]: 'Chyba',
}

export interface SagaWorkflowNodeDto {
  state: string
  kind: SagaNodeKind
  label: string
  status: SagaNodeStatus
}

export interface SagaWorkflowEdgeDto {
  from: string
  to: string
  label?: string
}

export interface SagaWorkflowChildDto {
  relationLabel: string
  graph: SagaWorkflowGraphDto
}

export interface SagaWorkflowGraphDto {
  id: string
  rootSagaId: string
  sagaType: string
  eventType: string
  currentState: string
  isTerminal: boolean
  // True when currentState matches no node of the definition (definition drift) — no Active node is rendered.
  currentStateUnknown: boolean
  partnerId?: number
  nodes: SagaWorkflowNodeDto[]
  edges: SagaWorkflowEdgeDto[]
  steps: SagaAuditLogDto[]
  children: SagaWorkflowChildDto[]
  fifoPredecessorId?: string
  fifoSuccessorId?: string
}

// The static state machine with no run attached (every node Pending) — the "before execution" diagram.
export interface SagaWorkflowDefinitionDto {
  sagaType: string
  nodes: SagaWorkflowNodeDto[]
  edges: SagaWorkflowEdgeDto[]
}
